import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  DataTable,
  InlineStack,
  Layout,
  Modal,
  Page,
  Select,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useVerifiedShopGeneration } from "../../ShopGenerationBoundary";
import { ApiError } from "../../api/client";
import { listLocations } from "../../api/locations";
import { getMe } from "../../api/me";
import {
  PURCHASE_ORDER_EMAIL_ATTACHMENT_LIMITS,
  PURCHASE_ORDER_EMAIL_ATTACHMENT_TYPES,
  listOrderableVariants,
  listSuppliers,
  type PurchaseOrderEmailAttachment,
  type PurchaseOrderLine,
} from "../../api/purchase_orders";
import {
  emailSavedPurchaseOrderDraft,
  getSavedDraftEmailPreview,
  getSavedPurchaseOrderDraft,
  listSavedPurchaseOrderDrafts,
  materializeSavedPurchaseOrderDraft,
  placeSavedPurchaseOrderDraft,
  updateSavedPurchaseOrderDraft,
  canonicalSavedDraftSourceId,
  type SavedDraftEmailPreview,
  type SavedDraftItem,
  type SavedDraftSourceId,
  type SavedDraftSummary,
  type SavedDraftTaxResolution,
  type SavedDraftUpdateInput,
  type SavedPurchaseOrderDraft,
} from "../../api/saved_purchase_order_drafts";
import {
  canonicalStockyPublicBigIntId,
  isStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "../../api/stocky_public_ids";
import { currentLocale, t } from "../../lib/i18n";
import { useLocationLimit } from "../../lib/useLocationLimit";
import {
  clearPersistentIdempotencyAttempt,
  persistentIdempotencyAttempt,
  type PersistentIdempotencyAttempt,
} from "../../utils/idempotency";
import { toLocationSelectOptions } from "../../utils/locationOptions";
import {
  clearSavedPurchaseOrderEmailAttempt,
  persistSavedPurchaseOrderEmailAttempt,
  readSavedPurchaseOrderEmailAttempt,
  SavedPurchaseOrderEmailAttemptConflictError,
  SavedPurchaseOrderEmailAttemptStorageError,
  type PendingSavedPurchaseOrderEmailAttempt,
} from "../../utils/savedPurchaseOrderEmailAttempt";
import { PurchaseOrderModeSwitch } from "./PurchaseOrderModeSwitch";

const MATERIALIZE_SCOPE = "purchase-order:saved-draft:materialize";
const PLACE_SCOPE = "purchase-order:saved-draft:place";

function attachmentTypeForFile(file: File): string | null {
  const extension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
  const expected = PURCHASE_ORDER_EMAIL_ATTACHMENT_TYPES[extension];
  return expected !== undefined && file.type === expected ? expected : null;
}

async function readPurchaseOrderEmailAttachment(
  file: File,
): Promise<PurchaseOrderEmailAttachment> {
  const contentType = attachmentTypeForFile(file);
  if (contentType === null) {
    throw new Error(
      t("添付できるのは PDF、CSV、Excel、Word、PNG、JPEG のみです。"),
    );
  }
  if (!file.size) throw new Error(t("空の添付ファイルは送信できません。"));
  if (file.size > PURCHASE_ORDER_EMAIL_ATTACHMENT_LIMITS.maxBytesPerFile) {
    throw new Error(t("添付ファイル1件は 1 MiB 以下にしてください。"));
  }
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(new Error(t("添付ファイルを読み取れませんでした。")));
    reader.onload = () =>
      typeof reader.result === "string"
        ? resolve(reader.result)
        : reject(new Error(t("添付ファイルを読み取れませんでした。")));
    reader.readAsDataURL(file);
  });
  const separator = dataUrl.indexOf(",");
  if (separator < 0) {
    throw new Error(t("添付ファイルを読み取れませんでした。"));
  }
  return {
    filename: file.name,
    content_type: contentType,
    content: dataUrl.slice(separator + 1),
  };
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.detail;
  return error instanceof Error ? error.message : String(error);
}

function isDefinitiveFailure(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    ![408, 425, 429].includes(error.status)
  );
}

function isDefinitiveEmailFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (
    [
      "delivery_rejected",
      "intent_expired",
      "draft_stale",
      "draft_not_found",
      "draft_invalid",
      "attachment_invalid",
      "email_currency_unknown",
      "invalid_request",
      "saved_draft_not_editable",
      "saved_draft_source_archived",
      "saved_draft_source_ambiguous",
      "saved_draft_source_missing",
      "saved_draft_source_money_unresolved",
      "saved_draft_source_ordered",
    ].includes(error.code ?? "")
  ) {
    return true;
  }
  if (
    [
      "delivery_in_progress",
      "delivery_unknown",
      "delivery_ambiguous",
      "delivery_unresolved",
      "idempotency_key_reused",
    ].includes(error.code ?? "")
  ) {
    return false;
  }
  return (
    error.status >= 400 &&
    error.status < 500 &&
    ![408, 409, 425, 429].includes(error.status)
  );
}

function isDefinitivePlaceFailure(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    ![408, 409, 425, 429].includes(error.status)
  );
}

function readinessBadge(summary: SavedDraftSummary) {
  if (summary.readiness === "ready") {
    return <Badge tone="success">{t("送信準備済み")}</Badge>;
  }
  if (summary.readiness === "source_changed") {
    return <Badge tone="critical">{t("原本が変更されています")}</Badge>;
  }
  return <Badge tone="attention">{t("確認が必要")}</Badge>;
}

function statusProvenance(summary: SavedDraftSummary): string {
  return summary.source_status_provenance === "observed"
    ? t("Stockyで確認済み")
    : t("保存内容から推定");
}

function sourceStatus(summary: SavedDraftSummary): string {
  if (summary.source_status === "draft") return t("下書き");
  if (summary.source_status === "ordered") return t("発注済み");
  if (summary.source_status === "archived") return t("アーカイブ");
  return t("状態不明");
}

const NON_MATERIALIZABLE_BLOCKERS = new Set([
  "monetary_adjustments_unresolved",
  "source_identity_ambiguous",
  "source_line_coverage_missing",
  "source_lines_invalid",
  "too_many_lines",
]);

function SavedDraftList({
  onOpen,
  focusSourceId,
}: {
  onOpen: (id: StockyPublicBigIntId) => void;
  focusSourceId: SavedDraftSourceId | null;
}) {
  const queryClient = useQueryClient();
  const { shopGeneration } = useVerifiedShopGeneration();
  const attempts = useRef(new Map<string, PersistentIdempotencyAttempt>());
  const query = useQuery({
    queryKey: ["saved-purchase-order-drafts"],
    queryFn: () => listSavedPurchaseOrderDrafts(),
  });
  const focusedSourceQuery = useQuery({
    queryKey: ["saved-purchase-order-drafts", "source", focusSourceId],
    queryFn: () => listSavedPurchaseOrderDrafts(focusSourceId!),
    enabled: focusSourceId !== null,
  });
  const materializeMutation = useMutation({
    mutationFn: ({ summary }: { summary: SavedDraftSummary }) => {
      const fingerprint = `${summary.source_id}:${summary.source_sha}`;
      const attempt = persistentIdempotencyAttempt({
        scope: MATERIALIZE_SCOPE,
        shopGeneration,
        previous: attempts.current.get(summary.source_id) ?? null,
        fingerprint,
      });
      attempts.current.set(summary.source_id, attempt);
      return materializeSavedPurchaseOrderDraft(
        summary.source_id,
        summary.source_sha,
        attempt.key,
      ).then((draft) => ({ draft, attempt, sourceId: summary.source_id }));
    },
    onSuccess: ({ draft, attempt, sourceId }) => {
      clearPersistentIdempotencyAttempt(MATERIALIZE_SCOPE, attempt);
      attempts.current.delete(sourceId);
      void queryClient.invalidateQueries({
        queryKey: ["saved-purchase-order-drafts"],
      });
      onOpen(draft.id);
    },
    onError: (error, { summary }) => {
      const attempt = attempts.current.get(summary.source_id);
      if (attempt && isDefinitiveFailure(error)) {
        clearPersistentIdempotencyAttempt(MATERIALIZE_SCOPE, attempt);
        attempts.current.delete(summary.source_id);
      }
      if (error instanceof ApiError && [409, 412].includes(error.status)) {
        void queryClient.invalidateQueries({
          queryKey: ["saved-purchase-order-drafts"],
        });
      }
    },
  });

  if (
    query.isLoading ||
    (focusSourceId !== null && focusedSourceQuery.isLoading)
  ) {
    return <SkeletonBodyText lines={8} />;
  }
  if (query.isError) {
    return (
      <Banner
        tone="critical"
        title={t("保存済み下書きを取得できませんでした")}
        action={{ content: t("再試行"), onAction: () => void query.refetch() }}
      >
        {errorMessage(query.error)}
      </Banner>
    );
  }
  if (focusSourceId !== null && focusedSourceQuery.isError) {
    return (
      <Banner
        tone="critical"
        title={t("指定されたStocky原本を確認できませんでした")}
        action={{
          content: t("再試行"),
          onAction: () => void focusedSourceQuery.refetch(),
        }}
      >
        {errorMessage(focusedSourceQuery.error)}
      </Banner>
    );
  }

  const focusedDraft = focusedSourceQuery.data?.drafts[0] ?? null;
  const allDrafts = [
    ...(focusedDraft === null ? [] : [focusedDraft]),
    ...(query.data?.drafts ?? []).filter(
      (draft) => draft.source_id !== focusedDraft?.source_id,
    ),
  ];
  const drafts = focusSourceId
    ? [
        ...allDrafts.filter((draft) => draft.source_id === focusSourceId),
        ...allDrafts.filter((draft) => draft.source_id !== focusSourceId),
      ]
    : allDrafts;
  if (drafts.length === 0) {
    return (
      <Card>
        <BlockStack gap="200">
          <Text as="h2" variant="headingMd">
            {t("保存済み下書きはありません")}
          </Text>
          <Text as="p" tone="subdued">
            {t(
              "Stockyから保存した発注書がある場合は、ここからUreyukiBoxの注文書として編集できます。",
            )}
          </Text>
        </BlockStack>
      </Card>
    );
  }

  return (
    <BlockStack gap="300">
      {materializeMutation.isError && (
        <Banner
          tone="critical"
          title={t("注文書の作業用下書きを開けませんでした")}
        >
          {errorMessage(materializeMutation.error)}
        </Banner>
      )}
      {query.data?.truncated && (
        <Banner tone="warning" title={t("一部の保存済み下書きだけを表示しています")}>
          {t("一覧の上限を超えた下書きがあります。目的の注文書が見つからない場合はデータの発注書原本から開いてください。")}
        </Banner>
      )}
      {drafts.map((summary) => {
        const cannotMaterialize =
          summary.id === null &&
          summary.blockers.some((blocker) =>
            NON_MATERIALIZABLE_BLOCKERS.has(blocker.code),
          );
        return (
          <Card key={summary.source_id}>
            <BlockStack gap="300">
              <InlineStack
                align="space-between"
                blockAlign="start"
                gap="300"
                wrap
              >
                <BlockStack gap="100">
                  <InlineStack gap="200" blockAlign="center" wrap>
                    <Text as="h2" variant="headingMd">
                      {summary.source_number || t("番号なしの注文書")}
                    </Text>
                    {readinessBadge(summary)}
                    {summary.id !== null && (
                      <Badge>{t("作業用下書き作成済み")}</Badge>
                    )}
                  </InlineStack>
                  <Text as="p">
                    {t("仕入先")}: {summary.supplier_name || t("未設定")}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t("通貨")}: {summary.currency || "—"} ·{" "}
                    {t("明細 {count}件", { count: summary.item_count })}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t("原本の状態")}: {sourceStatus(summary)} ({statusProvenance(summary)})
                  </Text>
                </BlockStack>
                <Button
                  variant="primary"
                  loading={
                    materializeMutation.isPending &&
                    materializeMutation.variables.summary.source_id ===
                      summary.source_id
                  }
                  disabled={materializeMutation.isPending || cannotMaterialize}
                  onClick={() =>
                    summary.id === null
                      ? materializeMutation.mutate({ summary })
                      : onOpen(summary.id)
                  }
                >
                  {t("注文書として編集")}
                </Button>
              </InlineStack>
              {summary.blockers.length > 0 && (
                <Banner
                  tone="warning"
                  title={t("送信前に確認・修正が必要です")}
                >
                  <ul>
                    {summary.blockers.map((blocker, index) => (
                      <li key={`${blocker.code}:${blocker.item_id ?? index}`}>
                        {blocker.message}
                      </li>
                    ))}
                  </ul>
                </Banner>
              )}
            </BlockStack>
          </Card>
        );
      })}
    </BlockStack>
  );
}

interface EditableItem {
  key: string;
  id?: StockyPublicBigIntId;
  variant_id: StockyPublicBigIntId | null;
  sku: string | null;
  title: string | null;
  quantity: string;
  unit_price: string;
  tax_resolution: SavedDraftTaxResolution;
  tax_rate_id: StockyPublicBigIntId | null;
  tax_rate_name: string | null;
  tax_rate: string | null;
  inventory_effect_available: boolean;
}

interface DraftForm {
  supplier_id: StockyPublicBigIntId | null;
  location_id: StockyPublicBigIntId | null;
  issue_date: string;
  reference_number: string;
  expected_at: string;
  note: string;
  currency: string;
  items: EditableItem[];
}

function validSnapshotTax(
  rateName: string | null,
  rate: string | null,
): boolean {
  if (!rateName?.trim() || rate === null) return false;
  if (!/^(?:0(?:\.[0-9]{1,4})?|1(?:\.0{1,4})?)$/.test(rate)) return false;
  const value = Number(rate);
  return Number.isFinite(value) && value >= 0 && value <= 1;
}

function editableItem(
  item: SavedDraftItem,
  activeTaxRateIds: ReadonlySet<StockyPublicBigIntId>,
): EditableItem {
  const snapshotTaxIsKnown = validSnapshotTax(
    item.tax_rate_name,
    item.tax_rate,
  );
  const activeTaxRateId =
    item.tax_rate_id !== null && activeTaxRateIds.has(item.tax_rate_id)
      ? item.tax_rate_id
      : null;
  const taxResolution =
    item.tax_resolution === "rate" &&
    activeTaxRateId === null &&
    !snapshotTaxIsKnown
      ? "unresolved"
      : item.tax_resolution;
  return {
    key: `saved:${item.id}`,
    id: item.id,
    variant_id: item.variant_id,
    sku: item.sku,
    title: item.title,
    quantity: String(item.quantity),
    unit_price: item.unit_price,
    tax_resolution: taxResolution,
    // An inactive/deleted catalog rate cannot be submitted by id. Preserve its
    // immutable name/rate snapshot as an order-local direct rate instead.
    tax_rate_id: activeTaxRateId,
    tax_rate_name: item.tax_rate_name,
    tax_rate: item.tax_rate,
    inventory_effect_available: item.inventory_effect_available,
  };
}

function formFromDraft(draft: SavedPurchaseOrderDraft): DraftForm {
  const activeTaxRateIds = new Set(draft.tax_rates.map((rate) => rate.id));
  return {
    supplier_id: draft.supplier_id,
    location_id: draft.location_id,
    issue_date: draft.issue_date ?? "",
    reference_number: draft.reference_number ?? "",
    expected_at: draft.expected_at ?? "",
    note: draft.note ?? "",
    currency: draft.currency ?? "",
    items: draft.items.map((item) => editableItem(item, activeTaxRateIds)),
  };
}

function formFingerprint(form: DraftForm): string {
  return JSON.stringify(form);
}

function validQuantity(value: string): boolean {
  if (!/^[1-9][0-9]*$/.test(value)) return false;
  const quantity = Number(value);
  return Number.isSafeInteger(quantity) && quantity <= 2_147_483_647;
}

function validMoney(value: string): boolean {
  return /^(?:0|[1-9][0-9]{0,11})(?:\.[0-9]{1,2})?$/.test(value);
}

function validCurrency(value: string): boolean {
  return /^[A-Za-z]{3}$/.test(value.trim());
}

function validOptionalCurrency(value: string): boolean {
  return value.trim() === "" || validCurrency(value);
}

function snapshotTaxLabel(item: EditableItem): string {
  const numericRate = item.tax_rate === null ? Number.NaN : Number(item.tax_rate);
  const displayedRate = Number.isFinite(numericRate)
    ? new Intl.NumberFormat(currentLocale(), {
        style: "percent",
        maximumFractionDigits: 2,
      }).format(numericRate)
    : (item.tax_rate ?? "—");
  return t("Stocky原本の税率: {name}", {
    name: item.tax_rate_name?.trim() || displayedRate,
  });
}

function buildUpdateInput(form: DraftForm): SavedDraftUpdateInput | null {
  if (
    !validOptionalCurrency(form.currency) ||
    form.reference_number.length > 128 ||
    form.note.length > 2000 ||
    form.items.some(
      (item) => !validQuantity(item.quantity) || !validMoney(item.unit_price),
    )
  ) {
    return null;
  }
  return {
    supplier_id: form.supplier_id,
    issue_date: form.issue_date || null,
    reference_number: form.reference_number.trim() || null,
    location_id: form.location_id,
    currency: form.currency.trim()
      ? form.currency.trim().toUpperCase()
      : null,
    expected_at: form.expected_at || null,
    note: form.note.trim() || null,
    items: form.items.map((item) => ({
      ...(item.id === undefined ? {} : { id: item.id }),
      variant_id: item.variant_id,
      sku: item.sku,
      title: item.title,
      quantity: Number(item.quantity),
      unit_price: item.unit_price,
      tax_resolution: item.tax_resolution,
      tax_rate_id: item.tax_rate_id,
      tax_rate_name: item.tax_rate_name,
      tax_rate: item.tax_rate,
    })),
  };
}

function validationReasons(
  draft: SavedPurchaseOrderDraft,
  form: DraftForm,
  dirty: boolean,
): string[] {
  const reasons: string[] = [];
  if (dirty) reasons.push(t("下書きを保存してから送信・確定してください。"));
  for (const blocker of draft.blockers) {
    if (blocker.blocking) reasons.push(blocker.message);
  }
  if (form.supplier_id === null) reasons.push(t("仕入先を選択してください。"));
  if (form.items.length === 0) reasons.push(t("明細を1件以上追加してください。"));
  if (form.items.some((item) => !validQuantity(item.quantity))) {
    reasons.push(t("発注数量は1以上の整数で入力してください。"));
  }
  if (form.items.some((item) => !validMoney(item.unit_price))) {
    reasons.push(t("仕入単価は0以上の数値で入力してください。"));
  }
  if (form.items.some((item) => item.tax_resolution === "unresolved")) {
    reasons.push(t("すべての明細で税率または「税なし」を選択してください。"));
  }
  if (
    form.items.some(
      (item) =>
        item.tax_resolution === "rate" &&
        item.tax_rate_id === null &&
        !validSnapshotTax(item.tax_rate_name, item.tax_rate),
    )
  ) {
    reasons.push(t("選択した税率を確認してください。"));
  }
  return [...new Set(reasons)];
}

function SavedDraftEditor({
  draftId,
  onBack,
}: {
  draftId: StockyPublicBigIntId;
  onBack: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { shopGeneration } = useVerifiedShopGeneration();
  const locationLimit = useLocationLimit();
  const [form, setForm] = useState<DraftForm | null>(null);
  const [baseline, setBaseline] = useState("");
  const [search, setSearch] = useState("");
  const [emailPreview, setEmailPreview] = useState<SavedDraftEmailPreview | null>(null);
  const [placeConfirmationOpen, setPlaceConfirmationOpen] = useState(false);
  const [localEmailState, setLocalEmailState] = useState<
    "none" | "in_flight" | "unknown" | "sent" | null
  >(null);
  const emailAttempt = useRef<PendingSavedPurchaseOrderEmailAttempt | null>(null);
  const [recoverableEmailAttempt, setRecoverableEmailAttempt] =
    useState<PendingSavedPurchaseOrderEmailAttempt | null>(null);
  const [emailPersistenceError, setEmailPersistenceError] = useState<
    string | null
  >(null);
  const placeAttempt = useRef<PersistentIdempotencyAttempt | null>(null);
  const hydratedRevision = useRef<string | null>(null);

  const draftQuery = useQuery({
    queryKey: ["saved-purchase-order-drafts", draftId],
    queryFn: () => getSavedPurchaseOrderDraft(draftId),
  });
  const suppliersQuery = useQuery({ queryKey: ["po-suppliers"], queryFn: listSuppliers });
  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe });
  const canListLocations =
    locationLimit === undefined ||
    locationLimit.limit === null ||
    locationLimit.limit > 1;
  const locationsQuery = useQuery({
    queryKey: ["po-locations"],
    queryFn: listLocations,
    enabled: canListLocations,
  });

  const locationContextQuery = canListLocations ? locationsQuery : meQuery;
  const referenceDataReady =
    suppliersQuery.isSuccess && locationContextQuery.isSuccess;
  const referenceDataLoading =
    suppliersQuery.isLoading || locationContextQuery.isLoading;
  const referenceDataErrors = [
    ...(suppliersQuery.isError
      ? [`${t("仕入先")}: ${errorMessage(suppliersQuery.error)}`]
      : []),
    ...(locationContextQuery.isError
      ? [`${t("配送先")}: ${errorMessage(locationContextQuery.error)}`]
      : []),
  ];
  const retryReferenceData = () => {
    void suppliersQuery.refetch();
    if (canListLocations) void locationsQuery.refetch();
    else void meQuery.refetch();
  };
  const productQuery = useQuery({
    queryKey: ["saved-draft-product-search", search.trim(), form?.location_id ?? null],
    queryFn: () => listOrderableVariants(search, form?.location_id ?? null),
    enabled: search.trim().length > 0,
    placeholderData: keepPreviousData,
  });

  const refreshEmailState = () => {
    void draftQuery.refetch().then((result) => {
      if (!result.isError && result.data) {
        setLocalEmailState(result.data.email_state);
      }
    });
  };

  const dirty = form !== null && formFingerprint(form) !== baseline;
  useEffect(() => {
    if (!draftQuery.data) return;
    const revisionKey = `${draftQuery.data.id}:${draftQuery.data.revision}`;
    if (hydratedRevision.current === revisionKey) return;
    // A background refetch may discover a newer revision. Never replace local
    // edits until the merchant explicitly accepts the conflict reload.
    if (dirty) return;
    const next = formFromDraft(draftQuery.data);
    setForm(next);
    setBaseline(formFingerprint(next));
    hydratedRevision.current = revisionKey;
  }, [draftId, draftQuery.data, dirty]);
  useEffect(() => {
    setLocalEmailState(null);
    setForm(null);
    setBaseline("");
    hydratedRevision.current = null;
  }, [draftId]);

  useEffect(() => {
    try {
      const pending = readSavedPurchaseOrderEmailAttempt(
        shopGeneration,
        draftId,
      );
      emailAttempt.current = pending;
      setRecoverableEmailAttempt(pending);
      setEmailPersistenceError(null);
    } catch {
      emailAttempt.current = null;
      setRecoverableEmailAttempt(null);
      setEmailPersistenceError(
        t(
          "保存済みの送信内容を安全に確認できません。ブラウザのストレージ設定を確認してください。",
        ),
      );
    }
  }, [draftId, shopGeneration]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const saveMutation = useMutation({
    mutationFn: ({ draft, input }: { draft: SavedPurchaseOrderDraft; input: SavedDraftUpdateInput }) =>
      updateSavedPurchaseOrderDraft(draft.id, draft.revision, input),
    onSuccess: (saved) => {
      queryClient.setQueryData(["saved-purchase-order-drafts", saved.id], saved);
      const next = formFromDraft(saved);
      setForm(next);
      setBaseline(formFingerprint(next));
      hydratedRevision.current = `${saved.id}:${saved.revision}`;
      void queryClient.invalidateQueries({ queryKey: ["saved-purchase-order-drafts"] });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 412) {
        // Keep the local form intact. The authoritative revision is fetched so
        // an explicit reload can adopt it without an endless stale-CAS retry.
        void draftQuery.refetch();
        return;
      }
      if (error instanceof ApiError && error.status === 409) {
        // A stale tab may have missed an accepted/sent email. Freeze first,
        // then replace the provisional state from the locked server detail.
        setLocalEmailState("unknown");
        refreshEmailState();
      }
    },
  });

  const previewMutation = useMutation({
    mutationFn: () => getSavedDraftEmailPreview(draftId),
    onSuccess: (preview) => {
      emailAttempt.current = null;
      setEmailPersistenceError(null);
      setEmailPreview(preview);
    },
    onError: (error) => {
      if (
        error instanceof ApiError &&
        error.code === "email_currency_unknown"
      ) {
        void draftQuery.refetch();
      }
      if (
        error instanceof ApiError &&
        [
          "delivery_unresolved",
          "saved_draft_already_delivered",
          "saved_draft_email_locked",
        ].includes(error.code ?? "")
      ) {
        setLocalEmailState("unknown");
        refreshEmailState();
        void queryClient.invalidateQueries({
          queryKey: ["purchase-order-email-deliveries"],
        });
      }
    },
  });
  const emailMutation = useMutation({
    mutationFn: ({ attempt }: { attempt: PendingSavedPurchaseOrderEmailAttempt }) => {
      setLocalEmailState("in_flight");
      return emailSavedPurchaseOrderDraft(
        draftId,
        attempt.preview,
        attempt.key,
      ).then((result) => ({ result, attempt }));
    },
    onSuccess: ({ result, attempt }) => {
      clearSavedPurchaseOrderEmailAttempt(attempt);
      emailAttempt.current = null;
      setRecoverableEmailAttempt(null);
      setEmailPersistenceError(null);
      setEmailPreview(null);
      setLocalEmailState("sent");
      queryClient.setQueryData<SavedPurchaseOrderDraft>(
        ["saved-purchase-order-drafts", draftId],
        (current) =>
          current
            ? {
                ...current,
                email_state: "sent",
                last_delivery_id: result.delivery_id,
              }
            : current,
      );
      void queryClient.invalidateQueries({
        queryKey: ["purchase-order-email-deliveries"],
      });
    },
    onError: (error, { attempt }) => {
      if (isDefinitiveEmailFailure(error)) {
        clearSavedPurchaseOrderEmailAttempt(attempt);
        emailAttempt.current = null;
        setRecoverableEmailAttempt(null);
        setLocalEmailState("none");
      } else if (
        error instanceof ApiError &&
        error.code === "delivery_in_progress"
      ) {
        setLocalEmailState("in_flight");
      } else {
        setLocalEmailState("unknown");
      }
      setEmailPreview(null);
      refreshEmailState();
      void queryClient.invalidateQueries({
        queryKey: ["purchase-order-email-deliveries"],
      });
    },
  });

  const confirmSavedPurchaseOrderEmail = () => {
    if (emailPreview === null) return;
    try {
      const attempt = persistSavedPurchaseOrderEmailAttempt({
        shopGeneration,
        purchaseOrderId: draftId,
        preview: emailPreview,
        previous: emailAttempt.current,
      });
      emailAttempt.current = attempt;
      setRecoverableEmailAttempt(attempt);
      setEmailPersistenceError(null);
      emailMutation.mutate({ attempt });
    } catch (error) {
      if (
        error instanceof SavedPurchaseOrderEmailAttemptStorageError ||
        error instanceof SavedPurchaseOrderEmailAttemptConflictError
      ) {
        setEmailPersistenceError(
          t(
            "送信内容と操作キーをこのタブに安全に保存できなかったため、メールは送信していません。ブラウザのストレージ設定を確認してください。",
          ),
        );
        return;
      }
      throw error;
    }
  };

  const openSavedPurchaseOrderEmail = () => {
    if (recoverableEmailAttempt !== null) {
      emailAttempt.current = recoverableEmailAttempt;
      setEmailPreview(recoverableEmailAttempt.preview);
      return;
    }
    previewMutation.mutate();
  };

  const addSavedPurchaseOrderEmailAttachments = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (emailPreview === null || files.length === 0) return;
    if (emailAttempt.current !== null) {
      setEmailPersistenceError(
        t("送信受付後のメールは編集・添付変更できません。結果を確認してください。"),
      );
      return;
    }
    try {
      const additions = await Promise.all(
        files.map(readPurchaseOrderEmailAttachment),
      );
      const combined = [...(emailPreview.attachments ?? []), ...additions];
      if (combined.length > PURCHASE_ORDER_EMAIL_ATTACHMENT_LIMITS.maxCount) {
        throw new Error(t("添付は 3 件までです。"));
      }
      const decodedTotal = combined.reduce(
        (total, attachment) =>
          total + Math.floor((attachment.content.length * 3) / 4),
        0,
      );
      if (decodedTotal > PURCHASE_ORDER_EMAIL_ATTACHMENT_LIMITS.maxTotalBytes) {
        throw new Error(t("添付ファイル合計は 2 MiB 以下にしてください。"));
      }
      if (
        new Set(
          combined.map((attachment) => attachment.filename.toLocaleLowerCase()),
        ).size !== combined.length
      ) {
        throw new Error(t("同じ名前の添付ファイルは1件だけにしてください。"));
      }
      setEmailPreview({ ...emailPreview, attachments: combined });
      setEmailPersistenceError(null);
    } catch (error) {
      setEmailPersistenceError(
        error instanceof Error
          ? error.message
          : t("添付ファイルを追加できませんでした。"),
      );
    }
  };
  const placeMutation = useMutation({
    mutationFn: ({ draft }: { draft: SavedPurchaseOrderDraft }) => {
      const attempt = persistentIdempotencyAttempt({
        scope: PLACE_SCOPE,
        shopGeneration,
        previous: placeAttempt.current,
        fingerprint: `${draft.id}:${draft.revision}`,
      });
      placeAttempt.current = attempt;
      return placeSavedPurchaseOrderDraft(draft.id, draft.revision, attempt.key).then((placed) => ({ placed, attempt }));
    },
    onSuccess: ({ attempt }) => {
      clearPersistentIdempotencyAttempt(PLACE_SCOPE, attempt);
      placeAttempt.current = null;
      navigate("/placed-orders");
      // The saved detail no longer exists after placement. Remove it instead
      // of prefix-invalidating it: an active refetch would only receive 409.
      queryClient.removeQueries({
        queryKey: ["saved-purchase-order-drafts", draftId],
        exact: true,
      });
      void queryClient.invalidateQueries({
        queryKey: ["saved-purchase-order-drafts"],
        exact: true,
      });
      void queryClient.invalidateQueries({ queryKey: ["placed-orders"] });
    },
    onError: (error) => {
      if (placeAttempt.current && isDefinitivePlaceFailure(error)) {
        clearPersistentIdempotencyAttempt(PLACE_SCOPE, placeAttempt.current);
        placeAttempt.current = null;
      }
      void draftQuery.refetch();
    },
  });

  if (draftQuery.isLoading) return <SkeletonBodyText lines={12} />;
  if (!draftQuery.data) {
    return (
      <Banner
        tone="critical"
        title={t("保存済み下書きを表示できませんでした")}
        action={{ content: t("一覧へ戻る"), onAction: onBack }}
      >
        {draftQuery.error ? errorMessage(draftQuery.error) : null}
      </Banner>
    );
  }
  if (form === null) return <SkeletonBodyText lines={12} />;

  const draft = draftQuery.data;
  const emailState = localEmailState ?? draft.email_state;
  const placeBlockedByEmail =
    emailState === "in_flight" || emailState === "unknown";
  const editingFrozen = emailState !== "none";
  const editingDisabled =
    !referenceDataReady ||
    editingFrozen ||
    saveMutation.isPending ||
    previewMutation.isPending;
  const actionReasons = validationReasons(draft, form, dirty);
  const suppliers = suppliersQuery.data ?? [];
  const selectedSupplier =
    form.supplier_id === null
      ? null
      : suppliers.find((supplier) => supplier.id === form.supplier_id) ?? null;
  const supplierEmailNeedsSetup =
    form.supplier_id !== null &&
    suppliersQuery.isSuccess &&
    !selectedSupplier?.contact_email?.trim();
  const supplierEmailReason =
    form.supplier_id === null
      ? null
      : suppliersQuery.isLoading
        ? t("仕入先情報を確認しています。")
        : suppliersQuery.isError
          ? t("仕入先のメールアドレスを確認できません。")
          : !selectedSupplier?.contact_email?.trim()
            ? t("仕入先のメールアドレスを登録すると、発注内容を確認して送信できます。")
            : null;
  const emailOnlyReasons = [
    ...(validCurrency(form.currency)
      ? []
      : [t("通貨コードを設定すると送信できます。")]),
    ...(supplierEmailReason === null ? [] : [supplierEmailReason]),
  ];
  const emailReasons = [...actionReasons, ...emailOnlyReasons];
  const reviewedEmailIsValid =
    emailPreview !== null &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailPreview.to_email) &&
    emailPreview.subject.trim().length > 0 &&
    !/[\r\n]/.test(emailPreview.subject) &&
    emailPreview.text.trim().length > 0;
  const warningBlockers = draft.blockers.filter((blocker) => !blocker.blocking);
  const updateInput = buildUpdateInput(form);
  const saveConflict =
    saveMutation.error instanceof ApiError && saveMutation.error.status === 412;
  const listedSupplierOptions = suppliers.map((supplier) => ({
    label: supplier.name,
    value: canonicalStockyPublicBigIntId(supplier.id),
  }));
  const selectedSupplierValue =
    form.supplier_id === null
      ? null
      : canonicalStockyPublicBigIntId(form.supplier_id);
  const savedSupplierFallback =
    selectedSupplierValue !== null &&
    !listedSupplierOptions.some(
      (option) => option.value === selectedSupplierValue,
    )
      ? [
          {
            label: draft.supplier_name || t("保存済みの仕入先"),
            value: selectedSupplierValue,
          },
        ]
      : [];
  const supplierOptions = [
    { label: t("仕入先を選択"), value: "" },
    ...savedSupplierFallback,
    ...listedSupplierOptions,
  ];
  const primaryLocationId = meQuery.data?.locations?.primary_location_id ?? null;
  const primaryLocationName = meQuery.data?.locations?.primary_name ?? null;
  const listedLocationOptions = canListLocations
    ? toLocationSelectOptions(locationsQuery.data ?? [])
    : primaryLocationId && primaryLocationName
      ? [
          {
            label: primaryLocationName,
            value: canonicalStockyPublicBigIntId(primaryLocationId),
            disabled: false,
          },
        ]
      : [];
  const selectedLocationValue =
    form.location_id === null
      ? null
      : canonicalStockyPublicBigIntId(form.location_id);
  const savedLocationFallback =
    selectedLocationValue !== null &&
    !listedLocationOptions.some(
      (option) => option.value === selectedLocationValue,
    )
      ? [
          {
            label: draft.location_name || t("保存済みの配送先"),
            value: selectedLocationValue,
            disabled: false,
          },
        ]
      : [];
  const locationOptions = [
    { label: t("主拠点"), value: "" },
    ...savedLocationFallback,
    ...listedLocationOptions,
  ];

  const updateItem = (index: number, patch: Partial<EditableItem>) => {
    setForm((current) =>
      current === null
        ? current
        : {
            ...current,
            items: current.items.map((item, itemIndex) =>
              itemIndex === index ? { ...item, ...patch } : item,
            ),
          },
    );
  };
  const removeItem = (index: number) => {
    setForm((current) =>
      current === null
        ? current
        : { ...current, items: current.items.filter((_, itemIndex) => itemIndex !== index) },
    );
  };
  const addProduct = (line: PurchaseOrderLine) => {
    if (form.items.some((item) => item.variant_id === line.variant_id)) return;
    setForm((current) =>
      current === null
        ? current
        : {
            ...current,
            items: [
              ...current.items,
              {
                key: `new:${line.variant_id}`,
                variant_id: line.variant_id,
                sku: line.sku,
                title: line.title,
                quantity: "1",
                unit_price: line.unit_price,
                tax_resolution: line.tax_rate_id === null ? "unresolved" : "rate",
                tax_rate_id: line.tax_rate_id,
                tax_rate_name: line.tax_rate_name,
                tax_rate: line.tax_rate,
                inventory_effect_available: true,
              },
            ],
          },
    );
    setSearch("");
  };

  const confirmLeave = () =>
    !dirty || window.confirm(t("未保存の編集を破棄して移動しますか？"));
  const reloadLatestDraft = () => {
    if (!confirmLeave()) return;
    void draftQuery.refetch().then((result) => {
      if (result.isError || !result.data) return;
      const next = formFromDraft(result.data);
      setForm(next);
      setBaseline(formFingerprint(next));
      hydratedRevision.current = `${result.data.id}:${result.data.revision}`;
      saveMutation.reset();
    });
  };
  const back = () => {
    if (confirmLeave()) onBack();
  };

  const rows = form.items.map((item, index) => {
    const activeRate =
      item.tax_rate_id === null
        ? undefined
        : draft.tax_rates.find((rate) => rate.id === item.tax_rate_id);
    const hasSnapshotRate =
      item.tax_resolution === "rate" &&
      activeRate === undefined &&
      validSnapshotTax(item.tax_rate_name, item.tax_rate);
    const taxOptions = [
      { label: t("税率を選択"), value: "unresolved" },
      { label: t("税なし"), value: "none" },
      ...(hasSnapshotRate
        ? [{ label: snapshotTaxLabel(item), value: "snapshot-rate" }]
        : []),
      ...draft.tax_rates.map((rate) => ({ label: rate.name, value: `rate:${rate.id}` })),
    ];
    const taxValue =
      item.tax_resolution === "rate" && activeRate !== undefined
        ? `rate:${item.tax_rate_id}`
        : hasSnapshotRate
          ? "snapshot-rate"
          : item.tax_resolution;
    return [
      <BlockStack key={`${item.key}:product`} gap="050">
        <Text as="span">{item.title || t("商品名なし")}</Text>
        <Text as="span" variant="bodySm" tone="subdued">{item.sku || t("SKUなし")}</Text>
        {!item.inventory_effect_available && (
          <Badge tone="attention">{t("Shopify在庫へ反映不可")}</Badge>
        )}
      </BlockStack>,
      <TextField
        key={`${item.key}:qty`}
        label={t("数量")}
        labelHidden
        value={item.quantity}
        onChange={(value) => updateItem(index, { quantity: value })}
        disabled={editingDisabled}
        autoComplete="off"
        inputMode="numeric"
        error={validQuantity(item.quantity) ? undefined : t("1以上の整数")}
      />,
      <TextField
        key={`${item.key}:price`}
        label={t("仕入単価")}
        labelHidden
        value={item.unit_price}
        onChange={(value) => updateItem(index, { unit_price: value })}
        disabled={editingDisabled}
        autoComplete="off"
        inputMode="decimal"
        error={
          validMoney(item.unit_price)
            ? undefined
            : t("0以上・小数第2位までの数値")
        }
      />,
      <Select
        key={`${item.key}:tax`}
        label={t("税")}
        labelHidden
        options={taxOptions}
        value={taxValue}
        disabled={editingDisabled}
        onChange={(value) => {
          if (value === "snapshot-rate") return;
          if (value === "unresolved" || value === "none") {
            updateItem(index, {
              tax_resolution: value,
              tax_rate_id: null,
              tax_rate_name: null,
              tax_rate: null,
            });
            return;
          }
          const rate = draft.tax_rates.find((candidate) => `rate:${candidate.id}` === value);
          if (rate) {
            updateItem(index, {
              tax_resolution: "rate",
              tax_rate_id: rate.id,
              tax_rate_name: rate.name,
              tax_rate: rate.rate,
            });
          }
        }}
      />,
      <Button key={`${item.key}:remove`} tone="critical" disabled={editingDisabled} onClick={() => removeItem(index)}>
        {t("削除")}
      </Button>,
    ];
  });

  return (
    <BlockStack gap="400">
      <Modal
        open={emailPreview !== null}
        onClose={() => !emailMutation.isPending && setEmailPreview(null)}
        title={t("送信する注文書メールを確認")}
        primaryAction={{
          content:
            emailAttempt.current === null
              ? t("この内容で送信")
              : t("同じ内容で送信を再開"),
          onAction: confirmSavedPurchaseOrderEmail,
          loading: emailMutation.isPending,
          disabled:
            emailMutation.isPending ||
            !referenceDataReady ||
            !reviewedEmailIsValid,
        }}
        secondaryActions={[
          {
            content: emailMutation.isPending ? t("閉じる") : t("キャンセル"),
            onAction: () => setEmailPreview(null),
            disabled: emailMutation.isPending,
          },
        ]}
      >
        <Modal.Section>
          {emailPreview && (
            <BlockStack gap="400">
              <Banner tone="warning" title={t("表示中の本文だけを送信します")}>
                {t(
                  "宛先・件名・本文・添付を確認または編集してから送信してください。確認後に保存済み発注書が変わった場合は送信せず、最新内容の再確認を求めます。",
                )}
              </Banner>
              {!reviewedEmailIsValid && emailAttempt.current === null && (
                <Banner tone="critical">
                  {t(
                    "有効な宛先、改行を含まない件名、空白だけではない本文を入力してください。",
                  )}
                </Banner>
              )}
              {emailPersistenceError !== null && (
                <Banner tone="critical">{emailPersistenceError}</Banner>
              )}
              <TextField
                label={t("宛先")}
                value={emailPreview.to_email}
                type="email"
                autoComplete="email"
                disabled={emailAttempt.current !== null || emailMutation.isPending}
                onChange={(to_email) =>
                  setEmailPreview({ ...emailPreview, to_email })
                }
              />
              <BlockStack gap="100">
                <Text as="p" fontWeight="semibold">
                  {t("差出人")}
                </Text>
                <Text as="p">{emailPreview.from_email}</Text>
              </BlockStack>
              <TextField
                label={t("件名")}
                value={emailPreview.subject}
                autoComplete="off"
                disabled={emailAttempt.current !== null || emailMutation.isPending}
                onChange={(subject) =>
                  setEmailPreview({ ...emailPreview, subject })
                }
              />
              <TextField
                label={t("本文（商品・数量・金額を含む全文）")}
                value={emailPreview.text}
                multiline={16}
                autoComplete="off"
                disabled={emailAttempt.current !== null || emailMutation.isPending}
                onChange={(text) => setEmailPreview({ ...emailPreview, text })}
                helpText={t("本文は省略せず、そのまま送信されます。")}
              />
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">
                  {t("添付ファイル")}
                </Text>
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="p">
                    {t("purchase-order.pdf（発注書PDF）")}
                  </Text>
                  <Button
                    variant="plain"
                    tone={
                      emailPreview.include_purchase_order_pdf !== false
                        ? "critical"
                        : undefined
                    }
                    disabled={
                      emailAttempt.current !== null || emailMutation.isPending
                    }
                    onClick={() =>
                      setEmailPreview({
                        ...emailPreview,
                        include_purchase_order_pdf:
                          emailPreview.include_purchase_order_pdf === false,
                      })
                    }
                  >
                    {emailPreview.include_purchase_order_pdf !== false
                      ? t("外す")
                      : t("追加")}
                  </Button>
                </InlineStack>
                {(emailPreview.attachments ?? []).map((attachment) => (
                  <InlineStack
                    key={attachment.filename}
                    align="space-between"
                    blockAlign="center"
                  >
                    <Text as="p">{attachment.filename}</Text>
                    <Button
                      variant="plain"
                      tone="critical"
                      disabled={
                        emailAttempt.current !== null || emailMutation.isPending
                      }
                      onClick={() =>
                        setEmailPreview({
                          ...emailPreview,
                          attachments: (emailPreview.attachments ?? []).filter(
                            (candidate) =>
                              candidate.filename !== attachment.filename,
                          ),
                        })
                      }
                    >
                      {t("外す")}
                    </Button>
                  </InlineStack>
                ))}
                {!emailPreview.include_purchase_order_pdf &&
                  (emailPreview.attachments?.length ?? 0) === 0 && (
                    <Text as="p" tone="subdued">
                      {t("添付なしで送信します。")}
                    </Text>
                  )}
                <input
                  type="file"
                  multiple
                  aria-label={t("添付ファイルを選択")}
                  accept={Object.keys(PURCHASE_ORDER_EMAIL_ATTACHMENT_TYPES).join(
                    ",",
                  )}
                  disabled={emailAttempt.current !== null || emailMutation.isPending}
                  onChange={(event) =>
                    void addSavedPurchaseOrderEmailAttachments(event)
                  }
                />
                <Text as="p" tone="subdued">
                  {t(
                    "PDF、CSV、Excel、Word、PNG、JPEG。3件まで、1件1 MiB、合計2 MiBまで。",
                  )}
                </Text>
              </BlockStack>
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
      <Modal
        open={placeConfirmationOpen}
        onClose={() => !placeMutation.isPending && setPlaceConfirmationOpen(false)}
        title={t("この下書きを注文済みとしてマークしますか？")}
        primaryAction={{
          content: t("注文済みとしてマーク"),
          onAction: () => placeMutation.mutate({ draft }),
          loading: placeMutation.isPending,
          disabled:
            placeMutation.isPending ||
            !referenceDataReady ||
            placeBlockedByEmail,
        }}
        secondaryActions={[{ content: t("キャンセル"), onAction: () => setPlaceConfirmationOpen(false), disabled: placeMutation.isPending }]}
      >
        <Modal.Section>
          <BlockStack gap="200">
            <Text as="p">{t("明細 {count}件を確定し、入荷待ち数量へ反映します。", { count: form.items.length })}</Text>
            {emailState !== "sent" && (
              <Banner tone={placeBlockedByEmail ? "critical" : "warning"}>
                {emailState === "unknown"
                  ? t("発注メールの送信結果を照合するまで、注文済みとしてマークできません。")
                  : emailState === "in_flight"
                    ? t("発注メールの送信結果が確定するまで、注文済みとしてマークできません。")
                    : t("発注メールはまだ送信されていません。確定してもメールは送信されません。")}
              </Banner>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      <PurchaseOrderModeSwitch active="saved" confirmLeave={confirmLeave} />
      <InlineStack align="space-between" blockAlign="center" gap="200" wrap>
        <Button onClick={back}>{t("保存済み下書き一覧へ")}</Button>
        <InlineStack gap="200" wrap>
          <Button
            variant="primary"
            loading={saveMutation.isPending}
            disabled={!dirty || updateInput === null || !referenceDataReady || editingFrozen || saveMutation.isPending || saveConflict}
            onClick={() => updateInput && saveMutation.mutate({ draft, input: updateInput })}
          >
            {t("下書きを保存")}
          </Button>
          <Button
            loading={previewMutation.isPending}
            disabled={
              emailReasons.length > 0 ||
              !referenceDataReady ||
              (editingFrozen && recoverableEmailAttempt === null) ||
              previewMutation.isPending
            }
            onClick={openSavedPurchaseOrderEmail}
          >
            {t("発注メールを確認して送る")}
          </Button>
          <Button
            variant="primary"
            disabled={
              actionReasons.length > 0 ||
              !referenceDataReady ||
              placeBlockedByEmail ||
              previewMutation.isPending ||
              placeMutation.isPending
            }
            onClick={() => setPlaceConfirmationOpen(true)}
          >
            {t("注文済みとしてマーク")}
          </Button>
        </InlineStack>
      </InlineStack>

      <Banner tone="info" title={t("Stockyからコピーした作業用下書きです")}>
        <p>{t("編集内容はUreyukiBoxの作業用注文書に保存され、Stockyから保存した原本は変更されません。")}</p>
        <Button
          variant="plain"
          onClick={() => {
            if (confirmLeave()) {
              navigate(`/stocky-data/purchase-orders/${encodeURIComponent(draft.source_id)}`);
            }
          }}
        >
          {t("データの発注書原本を見る")}
        </Button>
      </Banner>

      {referenceDataLoading && (
        <Banner tone="info" title={t("仕入先と配送先を確認しています")}>
          {t("確認が完了するまで、下書きの保存・メール送信・注文済みへの変更はできません。")}
        </Banner>
      )}
      {referenceDataErrors.length > 0 && (
        <Banner
          tone="critical"
          title={t("仕入先または配送先を確認できませんでした")}
          action={{ content: t("再試行"), onAction: retryReferenceData }}
        >
          <BlockStack gap="100">
            {referenceDataErrors.map((message) => (
              <Text as="p" key={message}>
                {message}
              </Text>
            ))}
            <Text as="p">
              {t("安全のため、確認が完了するまで保存・メール送信・注文済みへの変更はできません。")}
            </Text>
          </BlockStack>
        </Banner>
      )}

      {emailState === "in_flight" && (
        <Banner tone="warning" title={t("発注メールを送信中です")}>
          {t("結果が確定するまで、下書きの編集と注文済みへの変更を停止しています。")}
        </Banner>
      )}
      {emailState === "unknown" && (
        <Banner tone="critical" title={t("発注メールの送信結果を確認してください")}>
          <p>{t("新しい内容や操作キーで再送せず、保存された送信記録を照合してください。下書きの編集と注文済みへの変更を停止しています。")}</p>
          <Button
            variant="plain"
            onClick={() => navigate(draft.last_delivery_id ? `/purchase-order-email-reconciliation/${draft.last_delivery_id}` : "/purchase-order-email-reconciliation")}
          >
            {t("送信状況を確認")}
          </Button>
        </Banner>
      )}
      {emailState === "sent" && (
        <Banner tone="success" title={t("発注メールを送信しました")}>
          {t("メール送信だけでは注文済みになりません。内容を確認して「注文済みとしてマーク」を実行してください。")}
        </Banner>
      )}
      {form.items.some((item) => !item.inventory_effect_available) && (
        <Banner tone="warning" title={t("Shopify在庫へ反映できない明細があります")}>
          {t("削除済みまたは未解決の商品は原本どおり明細とメールに残りますが、入荷時にShopify在庫へ反映できません。必要に応じて現在の商品へ置き換えるか削除してください。")}
        </Banner>
      )}
      {warningBlockers.length > 0 && (
        <Banner tone="warning" title={t("確認事項")}>
          <ul>
            {warningBlockers.map((blocker, index) => (
              <li key={`${blocker.code}:${blocker.item_id ?? index}`}>
                {blocker.message}
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {actionReasons.length > 0 && (
        <Banner tone="warning" title={t("送信・確定できない理由")}>
          <ul>{actionReasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>
        </Banner>
      )}
      {emailOnlyReasons.length > 0 && (
        <Banner
          tone="warning"
          title={t("メール送信できない理由")}
          action={
            supplierEmailNeedsSetup
              ? {
                  content: t("仕入先メールを登録"),
                  onAction: () => {
                    if (confirmLeave()) navigate("/suppliers");
                  },
                }
              : undefined
          }
        >
          <ul>{emailOnlyReasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>
        </Banner>
      )}
      {saveConflict && (
        <Banner
          tone="warning"
          title={t("注文書が別の画面で更新されました")}
          action={{ content: t("最新状態を再読み込み"), onAction: reloadLatestDraft }}
        >
          {t("最新状態を再読み込みし、内容を確認してからもう一度編集してください。")}
        </Banner>
      )}
      {emailPersistenceError !== null && emailPreview === null && (
        <Banner tone="critical" title={t("送信内容を安全に保存できません")}>
          {emailPersistenceError}
        </Banner>
      )}
      {((saveMutation.isError && !saveConflict) || previewMutation.isError || emailMutation.isError || placeMutation.isError) && (
        <Banner tone="critical" title={t("注文書の操作を完了できませんでした")}>
          {errorMessage((saveMutation.isError && !saveConflict ? saveMutation.error : null) ?? previewMutation.error ?? emailMutation.error ?? placeMutation.error)}
        </Banner>
      )}

      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center" wrap>
                <Text as="h2" variant="headingLg">{draft.source_number || t("番号なしの注文書")}</Text>
                {readinessBadge(draft)}
              </InlineStack>
              <DataTable
                columnContentTypes={["text", "numeric", "numeric", "text", "text"]}
                headings={[t("商品"), t("数量"), t("仕入単価"), t("税"), t("操作")]}
                rows={rows}
              />
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">{t("商品を追加")}</Text>
              <TextField
                label={t("現在のShopify商品を検索")}
                value={search}
                onChange={setSearch}
                disabled={editingDisabled}
                autoComplete="off"
                placeholder={t("商品名またはSKU")}
              />
              {productQuery.isFetching && <SkeletonBodyText lines={2} />}
              {search.trim() && productQuery.isError && (
                <Banner
                  tone="critical"
                  title={t("商品を検索できませんでした")}
                  action={{
                    content: t("再試行"),
                    onAction: () => void productQuery.refetch(),
                  }}
                >
                  {errorMessage(productQuery.error)}
                </Banner>
              )}
              {search.trim() && productQuery.isSuccess && (productQuery.data?.length ?? 0) === 0 && (
                <Banner
                  tone="info"
                  title={t("一致する現在の商品が見つかりません")}
                  action={{
                    content: t("ダッシュボードで今すぐ同期"),
                    onAction: () => {
                      if (confirmLeave()) navigate("/dashboard");
                    },
                  }}
                >
                  {t("Shopifyに商品があるのに候補へ表示されない場合は、ダッシュボードで「今すぐ同期」を実行してからもう一度検索してください。自動では対応付けません。")}
                </Banner>
              )}
              {(productQuery.isError ? [] : (productQuery.data ?? [])).slice(0, 8).map((line) => (
                <InlineStack key={line.variant_id} align="space-between" blockAlign="center" gap="200">
                  <Text as="span">{line.title || t("商品名なし")} ({line.sku || t("SKUなし")})</Text>
                  <Button disabled={editingDisabled || form.items.some((item) => item.variant_id === line.variant_id)} onClick={() => addProduct(line)}>
                    {form.items.some((item) => item.variant_id === line.variant_id) ? t("追加済み") : t("追加")}
                  </Button>
                </InlineStack>
              ))}
            </BlockStack>
          </Card>
        </Layout.Section>
        <Layout.Section variant="oneThird">
          <BlockStack gap="300">
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">{t("注文書の詳細")}</Text>
                <Select
                  label={t("仕入先")}
                  options={supplierOptions}
                  value={
                    form.supplier_id === null
                      ? ""
                      : canonicalStockyPublicBigIntId(form.supplier_id)
                  }
                  disabled={editingDisabled}
                  onChange={(value) => setForm({ ...form, supplier_id: value ? canonicalStockyPublicBigIntId(value) : null })}
                />
                <Select
                  label={t("配送先")}
                  options={locationOptions}
                  value={
                    form.location_id === null
                      ? ""
                      : canonicalStockyPublicBigIntId(form.location_id)
                  }
                  disabled={editingDisabled}
                  onChange={(value) => setForm({ ...form, location_id: value ? canonicalStockyPublicBigIntId(value) : null })}
                />
                <TextField label={t("発行日")} type="date" value={form.issue_date} disabled={editingDisabled} onChange={(value) => setForm({ ...form, issue_date: value })} autoComplete="off" />
                <TextField label={t("参照番号")} value={form.reference_number} disabled={editingDisabled} onChange={(value) => setForm({ ...form, reference_number: value })} maxLength={128} autoComplete="off" />
                <TextField label={t("希望納品日")} type="date" value={form.expected_at} disabled={editingDisabled} onChange={(value) => setForm({ ...form, expected_at: value })} autoComplete="off" />
                <TextField
                  label={t("通貨")}
                  value={form.currency}
                  disabled={editingDisabled}
                  onChange={(value) => setForm({ ...form, currency: value })}
                  maxLength={3}
                  error={
                    validOptionalCurrency(form.currency)
                      ? undefined
                      : t("3文字の英字コード")
                  }
                  autoComplete="off"
                />
                <TextField label={t("サプライヤーへのメモ")} value={form.note} disabled={editingDisabled} onChange={(value) => setForm({ ...form, note: value })} multiline={4} maxLength={2000} showCharacterCount autoComplete="off" />
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </BlockStack>
  );
}

export function SavedPurchaseOrderDrafts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawDraftId = searchParams.get("draft");
  const rawSourceId = searchParams.get("source");
  let focusSourceId: SavedDraftSourceId | null = null;
  try {
    focusSourceId =
      rawSourceId === null ? null : canonicalSavedDraftSourceId(rawSourceId);
  } catch {
    focusSourceId = null;
  }
  const draftId = isStockyPublicBigIntId(rawDraftId)
    ? canonicalStockyPublicBigIntId(rawDraftId)
    : null;
  const open = (id: StockyPublicBigIntId) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("view", "saved");
      next.set("draft", canonicalStockyPublicBigIntId(id));
      next.delete("source");
      return next;
    });
  };
  const back = () => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("view", "saved");
      next.delete("draft");
      return next;
    });
  };

  return (
    <Page
      title={t("注文書")}
      subtitle={t("保存した下書きを編集・送信するか、新しい注文書を作成します。")}
    >
      {rawDraftId !== null && draftId === null ? (
        <BlockStack gap="300">
          <PurchaseOrderModeSwitch active="saved" />
          <Banner tone="critical" title={t("指定された下書きは見つかりません")} action={{ content: t("一覧へ戻る"), onAction: back }} />
        </BlockStack>
      ) : draftId === null ? (
        <BlockStack gap="400">
          <PurchaseOrderModeSwitch active="saved" />
          {rawSourceId !== null && focusSourceId === null && (
            <Banner tone="warning" title={t("指定されたStocky原本を確認できませんでした")} />
          )}
          <SavedDraftList onOpen={open} focusSourceId={focusSourceId} />
        </BlockStack>
      ) : (
        <SavedDraftEditor draftId={draftId} onBack={back} />
      )}
    </Page>
  );
}
